import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSwipeable } from 'react-swipeable';
import { Image, Video } from '@fiverr-private/media';
import { Loader } from '@fiverr-private/feedback';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Box, theme } from '@fiverr-private/theme';
import { LightboxContentProps } from '../types';
import { useLightboxContext } from '../LightboxContext';
import { HEADER_HEIGHT } from '../LightboxHeader/constants';
import { ARROW_SIZE } from '../LightboxArrows/LightboxArrow/constants';
import { getImageDefaultAlt, getSourceType, isSourceLoadable } from './utils';
import NoPreviewAvailable from './NoPreviewAvailable';
import { imgVideoStylingProps } from './constants';

const LightboxContent = ({ dataTestId, audioTestId }: LightboxContentProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { selectedSourceIndex, nextSource, prevSource, sources, silentMode, setSilentMode } = useLightboxContext();

  // TODO: Avoid using 3d party "react-swipeable" pckg and create custom hook for it
  // https://github.com/fiverr/blocks/issues/1042
  const handlers = useSwipeable({
    onSwipedLeft: nextSource,
    onSwipedRight: prevSource,
  });

  const [isError, setIsError] = useState(false);
  const onError = useCallback(() => {
    setIsError(true);
    setIsLoading(false);
  }, []);

  const { selectedSource, selectedSourceType, isLoadable } = useMemo(() => {
    const selectedSource = sources[selectedSourceIndex];
    const selectedSourceType = getSourceType(selectedSource);
    const isLoadable = isSourceLoadable(selectedSource);
    setIsError(false);

    return {
      selectedSource,
      selectedSourceType,
      isLoadable,
    };
  }, [sources, selectedSourceIndex]);

  const onLoaded = useCallback(() => setIsLoading(false), []);

  const handleSilentMode = useCallback(
    ({ target }) => setSilentMode((target as HTMLMediaElement).muted),
    [setSilentMode]
  );

  const content = useMemo(() => {
    if (!selectedSource) {
      return null;
    }

    const key = `${selectedSourceType}_${selectedSourceIndex}`;
    switch (selectedSourceType) {
      case 'image':
        const alt = selectedSource.alt || getImageDefaultAlt(selectedSourceIndex);
        return isError ? (
          <NoPreviewAvailable downloadUrl={selectedSource.src} />
        ) : (
          <Image
            key={key}
            {...imgVideoStylingProps}
            src={selectedSource.src}
            alt={alt}
            onLoad={onLoaded}
            onError={onError}
          />
        );
      case 'video':
        return (
          <Video
            key={key}
            {...imgVideoStylingProps}
            src={selectedSource.src}
            controls
            autoPlay
            muted={silentMode}
            role="video"
            onLoadedData={onLoaded}
            onError={onError}
            onVolumeChange={handleSilentMode}
          />
        );
      case 'audio':
        return (
          <Box
            as="audio"
            maxWidth="480px"
            width={{
              default: `calc(100vw - ${theme.spacing['6']} * 2)`,
              sm: `calc(100vw - (${ARROW_SIZE} + ${theme.spacing['8']} + ${theme.spacing['6']}) * 2)`,
            }}
            key={key}
            src={selectedSource.src}
            data-testid={audioTestId}
            controls
            autoPlay={!silentMode}
            onLoadedData={onLoaded}
            onError={onError}
            onVolumeChange={handleSilentMode}
            onPause={handleSilentMode}
            onPlay={handleSilentMode}
          />
        );
      case 'pdf':
        return (
          <Box
            as="object"
            key={key}
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={`calc(100vh - ${HEADER_HEIGHT} * 3)`}
            width={{
              default: `calc(100vw - ${theme.spacing['6']} * 2)`,
              sm: `calc(100vw - (${ARROW_SIZE} + ${theme.spacing['8']} + ${theme.spacing['3']}) * 2)`,
            }}
            onLoad={onLoaded}
            onClick={(e) => e.stopPropagation()}
            data={selectedSource.src}
            type="application/pdf"
          >
            <NoPreviewAvailable setError={setIsError} downloadUrl={selectedSource.src} />
          </Box>
        );
      default:
        return <NoPreviewAvailable downloadUrl={selectedSource.src} />;
    }
  }, [
    selectedSource,
    audioTestId,
    selectedSourceType,
    selectedSourceIndex,
    onLoaded,
    onError,
    isError,
    silentMode,
    handleSilentMode,
  ]);

  useEffect(() => {
    setIsLoading(isError ? false : isLoadable);
  }, [isLoadable, content, isError]);

  return (
    <Stack
      width="100%"
      height="100%"
      gap="0"
      alignItems="center"
      justifyContent="center"
      userSelect="none"
      data-testid={dataTestId}
      {...handlers}
    >
      {content}
      {isLoading && (
        <Container position="absolute">
          <Loader color="grey_600" />
        </Container>
      )}
    </Stack>
  );
};

LightboxContent.displayName = 'LightboxContent';

export default LightboxContent;
